import { requestNativeRefresh, resolveNativeRefreshMode } from './nativeRefreshBroker';
import { createMessengerClient } from "./messenger/messengerClient";
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import {
  exportSessionToNative,
  getBrowserSessionProtocolPayload,
  getFingerprint,
  getNativeSessionModeHeaders,
  getStoredRefreshToken,
  isInWebView,
  persistRefreshToken,
} from "./authSessionBridge";

const IDP_ORIGIN = process.env.REACT_APP_IDP_ORIGIN || "";
export const MESSENGER_WORKSPACE_ENABLED = process.env.REACT_APP_MESSENGER_V2_ENABLED !== "false";

export type ChatPreferences = {
  isMuted?: boolean;
  mutedUntil?: string | null;
  chatTheme?: string | null;
  compactMode?: boolean;
  autoDownloadPhotos?: boolean;
  autoDownloadVideos?: boolean;
  autoDownloadFiles?: boolean;
  autoDownloadVoice?: boolean;
  isPinned?: boolean;
  pinnedAt?: string | null;
  isImportant?: boolean;
  importantAt?: string | null;
};

export type ChatParticipant = {
  avatarUrl?: string | null;
  id: number;
  userId: number | null;
  phone: string | null;
  displayName: string | null;
  status: "active" | "invited";
  role: "owner" | "member";
  isMe: boolean;
  joinedAt: string | null;
  lastSeenAt?: string | null;
  preferences?: ChatPreferences;
};

export type ChatAttachment = {
  id: number;
  messageId?: number | null;
  chatId?: number | null;
  authorUserId?: number | null;
  text?: string;
  kind: "image" | "video" | "file" | "voice";
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  width?: number | null;
  height?: number | null;
  durationSec?: number | null;
  createdAt: string;
};

export type ChatMessage = {
  id: number;
  chatId: number;
  authorUserId: number | null;
  text: string;
  type: "text" | "media" | "file" | "voice" | "system";
  attachments: ChatAttachment[];
  links: string[];
  createdAt: string;
  updatedAt?: string | null;
  editedAt: string | null;
  deletedAt?: string | null;
  isDeleted?: boolean;
  replyTo?: ChatReplyPreview | null;
  forwardedFrom?: ChatForwardMeta | null;
  reactions?: ChatReaction[];
  isPinned?: boolean;
  isImportantForMe?: boolean;
};

export type ChatReplyPreview = {
  id: number;
  authorUserId: number | null;
  text: string;
  type: ChatMessage["type"];
  attachmentsCount: number;
  isDeleted: boolean;
};

export type ChatForwardMeta = {
  chatId: number | null;
  messageId: number | null;
  authorUserId: number | null;
  authorPhone?: string | null;
  forwardedAt?: string | null;
};

export type ChatReaction = {
  reaction: string;
  count: number;
  reactedByMe: boolean;
};

export type ChatPinnedMessage = {
  id: number;
  messageId: number | null;
  pinnedByUserId: number | null;
  createdAt: string;
  message: ChatMessage | null;
};

export type ChatReadState = {
  userId: number | null;
  lastReadMessageId: number | null;
  readAt: string;
  updatedAt: string;
};

export type ChatTypingState = {
  userId: number | null;
  isTyping: boolean;
  updatedAt: string;
};

export type ChatStreamEvent = {
  messages: ChatMessage[];
  reads: ChatReadState[];
  typing: ChatTypingState[];
  pinnedMessages?: ChatPinnedMessage[];
  cursor: {
    lastMessageId: number;
    serverTime: string;
  };
};

export type ChatItem = {
  participantCount?: number;
  isChannel?: boolean;
  canStartCall?: boolean;
  maxCallParticipants?: number;
  avatarUrl?: string | null;
  id: number;
  title: string | null;
  isGroup: boolean;
  createdAt: string;
  updatedAt: string;
  participants: ChatParticipant[];
  lastMessage: ChatMessage | null;
  unreadCount: number;
  isPinned?: boolean;
  pinnedAt?: string | null;
  isImportant?: boolean;
  importantAt?: string | null;
  preview?: string | null;
  mediaCounters?: {
    media?: number;
    files?: number;
    links?: number;
    voice?: number;
  };
};

export type ChatSettings = {
  chatId: number;
  title: string | null;
  isGroup: boolean;
  participantCount: number;
  preferences: ChatPreferences;
  pinnedMessages?: ChatPinnedMessage[];
  mediaCounters?: {
    media?: number;
    files?: number;
    links?: number;
    voice?: number;
  };
  participants?: ChatParticipant[];
};

type ChatListResponse = {
  items: ChatItem[];
};

type MessageListResponse = {
  items: ChatMessage[];
  hasMore: boolean;
};

let accessToken: string | null = null;
let accessExp = 0;
let cookieSessionValidUntil = 0;
let refreshing = false;
let waiters: Array<(ok: boolean) => void> = [];

function setAccessToken(token: string | null): void {
  accessToken = token;
  accessExp = token ? decodeExp(token) : 0;
}

function decodeExp(jwt: string): number {
  try {
    const parts = jwt.split(".");
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp : 0;
  } catch {
    return 0;
  }
}

function isTokenExpiredSoon(): boolean {
  if (!accessToken || !accessExp) {
    return cookieSessionValidUntil - Date.now() < 30000;
  }
  return accessExp * 1000 - Date.now() < 30000;
}

function getFp(): string | null {
  return getFingerprint() || null;
}

const idp: AxiosInstance = axios.create({
  baseURL: IDP_ORIGIN,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  timeout: 15000,
});

async function refreshOnce(force = false): Promise<boolean> {
  if (!force && !isTokenExpiredSoon()) return true;
  if (refreshing) return new Promise<boolean>((resolve) => waiters.push(resolve));

  refreshing = true;
  let succeeded = false;
  try {
    const fp = getFp();
    const rt = getStoredRefreshToken();
    const headers: Record<string, string> = {};
    if (fp) headers["X-CSRF-Token"] = fp;
    if (rt) headers["X-Refresh-Token"] = rt;
    if (isInWebView()) headers["X-Client"] = "mobile";
    Object.assign(headers, getNativeSessionModeHeaders());
    const refreshPayload = rt
      ? { refresh_token: rt }
      : getBrowserSessionProtocolPayload();

    const res = await resolveNativeRefreshMode() ? await requestNativeRefresh() : await idp.post(
      "/api/refresh",
      Object.keys(refreshPayload).length ? refreshPayload : undefined,
      {
        headers: Object.keys(headers).length ? headers : undefined,
        withCredentials: true,
      }
    );
    const token = (res.data as any)?.access_token || (res.data as any)?.token;
    setAccessToken(token || null);
    cookieSessionValidUntil = token ? 0 : Date.now() + 10 * 60 * 1000;
    const nextRefreshToken = typeof (res.data as any)?.refresh_token === "string"
      ? (res.data as any).refresh_token
      : rt;
    if (typeof (res.data as any)?.refresh_token === "string" && (res.data as any).refresh_token) {
      persistRefreshToken((res.data as any).refresh_token);
    }
    if (token || (res.data as any)?.refresh_token) {
      exportSessionToNative({
        access_token: token || "",
        refresh_token: nextRefreshToken,
        fp: fp || "",
        src: "staff_chat_refresh",
      });
    }
    succeeded = true;
    return true;
  } catch {
    setAccessToken(null);
    cookieSessionValidUntil = 0;
    return false;
  } finally {
    refreshing = false;
    waiters.splice(0).forEach((fn) => fn(succeeded));
  }
}

function makeIdpHttp(): AxiosInstance {
  const http = axios.create({
    baseURL: IDP_ORIGIN,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
    timeout: 15000,
  });

  http.interceptors.request.use(async (config: AxiosRequestConfig) => {
    await refreshOnce(false);
    config.headers = config.headers || {};
    if (["post", "put", "patch", "delete"].includes(String(config.method || "get").toLowerCase())) {
      const fp = getFp();
      if (fp) (config.headers as any)["X-CSRF-Token"] = fp;
    }
    if (accessToken) {
      (config.headers as any)["Authorization"] = "Bearer " + accessToken;
    } else {
      delete (config.headers as any)["Authorization"];
    }
    return config;
  });

  http.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const cfg = (error.config || {}) as AxiosRequestConfig & { _retried?: boolean };
      const status = error.response?.status;

      if (status === 401 && !cfg._retried) {
        cfg._retried = true;
        const ok = await refreshOnce(true);
        if (ok) {
          cfg.headers = cfg.headers || {};
          if (accessToken) {
            (cfg.headers as any)["Authorization"] = "Bearer " + accessToken;
          } else {
            delete (cfg.headers as any)["Authorization"];
          }
          return http.request(cfg);
        }
      }

      throw error;
    }
  );

  return http;
}

const httpIdp = makeIdpHttp();
export const messengerClient = createMessengerClient({ baseUrl: IDP_ORIGIN, request: async (path: string, options: any = {}) => (await httpIdp.request({ ...options, url: path, method: options.method || "GET", headers: { ...options.headers, "X-Device-Fingerprint": getFingerprint() || "" } })).data });

function normalizeAxiosError(error: unknown): never {
  const ax = error as AxiosError<any>;
  const status = ax.response?.status;
  const details = ax.response?.data;
  const err = new Error(
    "HTTP " +
      (status ?? "?") +
      (ax.message ? " - " + ax.message : "") +
      (details ? ": " + (typeof details === "string" ? details : JSON.stringify(details)) : "")
  );
  (err as any).status = status;
  (err as any).details = details;
  throw err;
}

export function resolveAssetUrl(path?: string | null): string {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = IDP_ORIGIN.replace(/\/$/, "");
  return normalized + (path.charAt(0) === "/" ? path : "/" + path);
}

export const chatApi = {
  async getProfile(): Promise<any> {
    const response = await httpIdp.get('/api/me');
    return response.data;
  },
  async listChats(limit = 40, offset = 0): Promise<ChatItem[]> {
    try {
      const res = await httpIdp.get<ChatListResponse>("/api/chats", { params: { limit, offset, compactParticipants: 1 } });
      return res.data?.items ?? [];
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async getChat(chatId: number): Promise<ChatItem> {
    try {
      const res = await httpIdp.get<ChatItem>("/api/chats/" + chatId);
      return res.data;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async createChat(payload: { title?: string; isGroup?: boolean; participantPhones: string[] }): Promise<ChatItem> {
    try {
      const res = await httpIdp.post<ChatItem>("/api/chats", payload);
      return res.data;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async inviteByPhone(chatId: number, phone: string, displayName?: string): Promise<ChatParticipant> {
    try {
      const res = await httpIdp.post<{ participant: ChatParticipant }>("/api/chats/" + chatId + "/invite", {
        phone,
        displayName,
      });
      return res.data.participant;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async listMessages(
    chatId: number,
    params?: { limit?: number; beforeId?: number; aroundId?: number }
  ): Promise<MessageListResponse> {
    try {
      const res = await httpIdp.get<MessageListResponse>("/api/chats/" + chatId + "/messages", { params });
      return res.data;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async searchMessages(
    chatId: number,
    params?: { q?: string; bucket?: string; authorUserId?: number; limit?: number }
  ): Promise<ChatMessage[]> {
    try {
      const res = await httpIdp.get<{ items: ChatMessage[] }>("/api/chats/" + chatId + "/messages/search", { params });
      return res.data?.items ?? [];
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async sendMessage(chatId: number, text: string, options?: { replyToMessageId?: number | null }): Promise<ChatMessage> {
    try {
      const res = await httpIdp.post<{ message: ChatMessage }>("/api/chats/" + chatId + "/messages", {
        text,
        replyToMessageId: options?.replyToMessageId || undefined,
      });
      return res.data.message;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async editMessage(chatId: number, messageId: number, text: string): Promise<ChatMessage> {
    try {
      const res = await httpIdp.put<{ message: ChatMessage }>("/api/chats/" + chatId + "/messages/" + messageId, { text });
      return res.data.message;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async deleteMessage(chatId: number, messageId: number): Promise<ChatMessage> {
    try {
      const res = await httpIdp.delete<{ message: ChatMessage }>("/api/chats/" + chatId + "/messages/" + messageId);
      return res.data.message;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async uploadFiles(
    chatId: number,
    files: File[] | FileList,
    options?: { text?: string; kind?: string; durationSec?: number | null; replyToMessageId?: number | null }
  ): Promise<ChatMessage> {
    try {
      const formData = new FormData();
      Array.prototype.slice.call(files || []).forEach((file: File) => {
        formData.append("files[]", file);
      });

      if (options?.text) formData.append("text", options.text);
      if (options?.kind) formData.append("kind", options.kind);
      if (options?.durationSec != null) formData.append("durationSec", String(options.durationSec));
      if (options?.replyToMessageId) formData.append("replyToMessageId", String(options.replyToMessageId));

      const res = await httpIdp.post<{ message: ChatMessage }>("/api/chats/" + chatId + "/attachments", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data.message;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async getChatSettings(chatId: number): Promise<ChatSettings> {
    try {
      const res = await httpIdp.get<{ settings: ChatSettings }>("/api/chats/" + chatId + "/settings");
      return res.data.settings;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async updateChatSettings(chatId: number, payload: ChatPreferences & { title?: string | null }): Promise<{ settings: ChatSettings; chat?: ChatItem }> {
    try {
      const res = await httpIdp.put<{ settings: ChatSettings; chat?: ChatItem }>("/api/chats/" + chatId + "/settings", payload);
      return res.data;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async getChatMedia(chatId: number, bucket = "media", limit = 60, offset = 0): Promise<Array<ChatAttachment | ChatMessage>> {
    try {
      const res = await httpIdp.get<{ items: Array<ChatAttachment | ChatMessage> }>("/api/chats/" + chatId + "/media", {
        params: { bucket, limit, offset },
      });
      return res.data?.items ?? [];
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async listPins(chatId: number): Promise<ChatPinnedMessage[]> {
    try {
      const res = await httpIdp.get<{ items: ChatPinnedMessage[] }>("/api/chats/" + chatId + "/pins");
      return res.data?.items ?? [];
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async pinMessage(chatId: number, messageId: number): Promise<ChatPinnedMessage[]> {
    try {
      const res = await httpIdp.post<{ items: ChatPinnedMessage[] }>("/api/chats/" + chatId + "/pins", { messageId });
      return res.data?.items ?? [];
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async unpinMessage(chatId: number, messageId: number): Promise<ChatPinnedMessage[]> {
    try {
      const res = await httpIdp.delete<{ items: ChatPinnedMessage[] }>("/api/chats/" + chatId + "/pins/" + messageId);
      return res.data?.items ?? [];
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async reactToMessage(chatId: number, messageId: number, reaction: string): Promise<ChatMessage> {
    try {
      const res = await httpIdp.post<{ message: ChatMessage }>("/api/chats/" + chatId + "/messages/" + messageId + "/reactions", { reaction });
      return res.data.message;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async deleteReaction(chatId: number, messageId: number): Promise<ChatMessage> {
    try {
      const res = await httpIdp.delete<{ message: ChatMessage }>("/api/chats/" + chatId + "/messages/" + messageId + "/reactions");
      return res.data.message;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async markMessageImportant(chatId: number, messageId: number): Promise<ChatMessage> {
    try {
      const res = await httpIdp.post<{ message: ChatMessage }>("/api/chats/" + chatId + "/messages/" + messageId + "/important");
      return res.data.message;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async unmarkMessageImportant(chatId: number, messageId: number): Promise<ChatMessage> {
    try {
      const res = await httpIdp.delete<{ message: ChatMessage }>("/api/chats/" + chatId + "/messages/" + messageId + "/important");
      return res.data.message;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async forwardMessage(targetChatId: number, sourceMessageId: number): Promise<ChatMessage> {
    try {
      const res = await httpIdp.post<{ message: ChatMessage }>("/api/chats/" + targetChatId + "/messages/forward", { sourceMessageId });
      return res.data.message;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async markRead(chatId: number, messageId: number): Promise<ChatReadState> {
    try {
      const res = await httpIdp.post<{ readState: ChatReadState }>("/api/chats/" + chatId + "/read", { messageId });
      return res.data.readState;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  async setTyping(chatId: number, isTyping: boolean): Promise<ChatTypingState> {
    try {
      const res = await httpIdp.post<{ typingState: ChatTypingState }>("/api/chats/" + chatId + "/typing", { isTyping });
      return res.data.typingState;
    } catch (error) {
      normalizeAxiosError(error);
    }
  },

  buildStreamUrl(chatId: number, lastMessageId: number): string {
    const normalized = IDP_ORIGIN.replace(/\/$/, "");
    const params = new URLSearchParams({ lastMessageId: String(Math.max(0, lastMessageId)) });
    return normalized + "/api/chats/" + chatId + "/stream?" + params.toString();
  },

  buildThreadsStreamUrl(): string {
    return IDP_ORIGIN.replace(/\/$/, "") + "/api/chats/stream?limit=100&compactParticipants=1";
  },

  resolveAssetUrl,
};
